import { createContext, KeyboardEvent, MouseEvent, ReactNode, useMemo, useRef, useState } from 'react'

import { Cell, Id } from '../types'
import { toCellId } from '../utils'
import { buildVirtualGrid, VirtualCell, VirtualGrid } from './virtualGrid'

export const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'] as const
export type ArrowKey = (typeof arrowKeys)[number]

export function isArrowKey(key: string): key is ArrowKey {
    return (arrowKeys as readonly string[]).includes(key)
}

export type SelectionMode = 'none' | 'single' | 'multiple'

export type Cursor = { rowId: Id; colId: Id } | undefined

export type DataGridContextType = {
    cursor: Cursor
    selectedCells: Cell[]
    virtualSelectedCells: VirtualCell[]
    setDefault: () => void
    onMouseUp: (event: MouseEvent, rowId: Id, colId: Id) => void
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
    getVirtualCell: VirtualGrid['getVirtualCell']
    blockKeyInput: boolean
    setBlockKeyInput: (isOpen: boolean) => void
    setSelection: (cells: Cell[]) => void
}

export const DataGridContext = createContext<DataGridContextType | undefined>(undefined)

type DataGridProviderProps<TRow extends { id: Id }, TCol extends { id: Id }> = {
    rows: TRow[]
    cols: TCol[]
    selectionMode?: SelectionMode
    allowDeselect?: boolean
    children: ReactNode
    selectedCells?: Cell[]
    onSelectionChange?: (cells: Cell[]) => void
}

const toCell = (cell: VirtualCell): Cell => ({ rowId: String(cell.rowId), colId: String(cell.colId) })

const resolveCursor = (cursorState: Cursor, selectedCells: Cell[]): Cursor => {
    if (cursorState === undefined) return selectedCells[0]
    if (selectedCells.length === 0) return cursorState

    const cursorId = toCellId(cursorState)
    const hasCursorInSelection = selectedCells.some(cell => toCellId(cell) === cursorId)
    return hasCursorInSelection ? cursorState : selectedCells[0]
}

type DataGridHandlers = {
    setDefault: () => void
    setSelection: (cells: Cell[]) => void
    onMouseUp: (event: MouseEvent, rowId: Id, colId: Id) => void
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
}

const NOOP_HANDLERS: DataGridHandlers = {
    setDefault: () => {},
    setSelection: () => {},
    onMouseUp: () => {},
    onKeyDown: () => {},
}

// Stable reference so the `selectedCellsProp ?? EMPTY_CELLS` fallback below does not
// allocate a new array each render and invalidate downstream useMemo dependencies.
const EMPTY_CELLS: Cell[] = []

export function DataGridProvider<TRow extends { id: Id }, TCol extends { id: Id }>({
    rows,
    cols,
    selectionMode = 'none',
    allowDeselect = true,
    children,
    selectedCells: selectedCellsProp,
    onSelectionChange,
}: DataGridProviderProps<TRow, TCol>) {
    const isSelectionControlled = selectedCellsProp !== undefined && onSelectionChange !== undefined
    const selectedCells = selectedCellsProp ?? EMPTY_CELLS
    const effectiveSelectionMode: SelectionMode = isSelectionControlled ? selectionMode : 'none'

    const [cursorState, setCursor] = useState<Cursor>(selectedCells[0])
    // when extending (shift-selection), we need to keep track of the original anchor, as the cursor is used to move the selection
    const [extendingAnchorState, setExtendingAnchor] = useState<Cursor>(cursorState)

    const [blockKeyInput, setBlockKeyInput] = useState(false)

    // Memoised because React Compiler does not memoise this call site, and the resulting
    // `getVirtualCell` reference would otherwise change every render, invalidating the
    // outer context value.
    const { move, jump, getVirtualCell, getRect } = useMemo(() => buildVirtualGrid(rows, cols), [rows, cols])

    const virtualSelectedCells = useMemo(
        () => selectedCells.map(cell => getVirtualCell(cell.rowId, cell.colId)).filter((c): c is VirtualCell => c !== undefined),
        [selectedCells, getVirtualCell]
    )

    const cursor = isSelectionControlled ? resolveCursor(cursorState, selectedCells) : undefined
    const extendingAnchor = isSelectionControlled ? resolveCursor(extendingAnchorState, selectedCells) : undefined

    const emitSelectionChange = (cells: Cell[]) => {
        if (!isSelectionControlled) return
        onSelectionChange(cells)
    }

    const setSelectionImpl: DataGridHandlers['setSelection'] = (cells: Cell[]) => {
        if (!isSelectionControlled) return
        setCursor(cells[0])
        setExtendingAnchor(cells[0])
        emitSelectionChange(cells)
    }

    const setDefaultImpl: DataGridHandlers['setDefault'] = () => {
        // prevent clearing selection if none selected
        if (selectedCells.length === 0) return
        setCursor(undefined)
        setExtendingAnchor(undefined)
        emitSelectionChange([])
    }

    const computeNextSelection = (currentSelection: Cell[], cell: Cell, isMultiMode: boolean): Cell[] => {
        if (!isMultiMode && !allowDeselect) return [cell]

        const id = toCellId(cell)
        const exists = currentSelection.some(selectedCell => toCellId(selectedCell) === id)

        if (!isMultiMode) {
            return exists ? [] : [cell]
        }

        if (!allowDeselect) {
            return exists ? currentSelection : [...currentSelection, cell]
        }

        return exists ? currentSelection.filter(selectedCell => toCellId(selectedCell) !== id) : [...currentSelection, cell]
    }

    const onMouseUpImpl: DataGridHandlers['onMouseUp'] = (event: MouseEvent, rowId: Id, colId: Id) => {
        if (blockKeyInput || effectiveSelectionMode === 'none') return

        const isMeta = event.metaKey || event.ctrlKey
        const isShift = event.shiftKey
        const multi = effectiveSelectionMode === 'multiple'

        const isExtending = multi && isShift
        const clicked: Cell = { rowId: String(rowId), colId: String(colId) }

        // Always move cursor
        setCursor(clicked)
        if (!isExtending || !extendingAnchor) {
            setExtendingAnchor(clicked)
        }

        if (!multi) {
            emitSelectionChange(computeNextSelection(selectedCells, clicked, false))
            return
        }

        if (isShift) {
            const anchor = extendingAnchor ?? clicked ?? selectedCells.at(-1) ?? clicked
            const virtualAnchorCell = getVirtualCell(anchor.rowId, anchor.colId)
            const virtualClickedCell = getVirtualCell(clicked.rowId, clicked.colId)

            if (!virtualAnchorCell || !virtualClickedCell) {
                emitSelectionChange(computeNextSelection(selectedCells, clicked, true))
                return
            }

            emitSelectionChange(getRect([virtualAnchorCell, virtualClickedCell]).map(toCell))
            return
        }

        emitSelectionChange(computeNextSelection(selectedCells, clicked, isMeta))
    }

    const onKeyDownImpl: DataGridHandlers['onKeyDown'] = (event: KeyboardEvent<HTMLDivElement>) => {
        if (blockKeyInput || effectiveSelectionMode === 'none') return
        if (!cursor) return

        const { key, metaKey, ctrlKey, shiftKey } = event
        if (!isArrowKey(key)) return

        event.preventDefault()

        const shouldJump = metaKey || ctrlKey
        const isExtending = shiftKey && effectiveSelectionMode === 'multiple'

        const virtualCursor = getVirtualCell(cursor.rowId, cursor.colId)

        if (!virtualCursor) return

        const keyActions: Record<ArrowKey, () => VirtualCell | undefined> = {
            ArrowUp: () => move(virtualCursor, 'up'),
            ArrowDown: () => move(virtualCursor, 'down'),
            ArrowLeft: () => (shouldJump ? jump(virtualCursor, 'left') : move(virtualCursor, 'left')),
            ArrowRight: () => (shouldJump ? jump(virtualCursor, 'right') : move(virtualCursor, 'right')),
        }

        const nextVirtualCell = keyActions[key]()

        if (!nextVirtualCell) return

        const nextCell = toCell(nextVirtualCell)
        setCursor(nextCell)

        if (!isExtending) {
            setExtendingAnchor(nextCell)
            emitSelectionChange([nextCell])
            return
        }

        const anchor = extendingAnchor ?? cursor
        const virtualAnchor = getVirtualCell(anchor.rowId, anchor.colId)

        if (!virtualAnchor) {
            emitSelectionChange(computeNextSelection(selectedCells, nextCell, true))
            return
        }

        emitSelectionChange(getRect([virtualAnchor, nextVirtualCell]).map(toCell))
    }

    // Latest-ref pattern: stable wrappers below always invoke the latest closures
    // (with up-to-date selectedCells/cursor/etc.) so the context value identity
    // does not depend on closure recreation. Required because React Compiler does
    // not memoise these arrow function definitions in this provider.
    const handlersRef = useRef<DataGridHandlers>(NOOP_HANDLERS)
    handlersRef.current = {
        setDefault: setDefaultImpl,
        setSelection: setSelectionImpl,
        onMouseUp: onMouseUpImpl,
        onKeyDown: onKeyDownImpl,
    }
    const stableHandlers = useMemo<DataGridHandlers>(
        () => ({
            setDefault: () => handlersRef.current.setDefault(),
            setSelection: cells => handlersRef.current.setSelection(cells),
            onMouseUp: (event, rowId, colId) => handlersRef.current.onMouseUp(event, rowId, colId),
            onKeyDown: event => handlersRef.current.onKeyDown(event),
        }),
        []
    )

    // Memoised so consumers (BodyRow etc.) only re-render on legitimate state changes
    // rather than on every provider render. React Compiler does not memoise this object
    // literal in this provider.
    const value: DataGridContextType = useMemo(
        () => ({
            cursor,
            setDefault: stableHandlers.setDefault,
            virtualSelectedCells,
            getVirtualCell,
            onKeyDown: stableHandlers.onKeyDown,
            onMouseUp: stableHandlers.onMouseUp,
            blockKeyInput,
            setBlockKeyInput,
            selectedCells,
            setSelection: stableHandlers.setSelection,
        }),
        [cursor, stableHandlers, virtualSelectedCells, getVirtualCell, blockKeyInput, setBlockKeyInput, selectedCells]
    )

    return <DataGridContext value={value}>{children}</DataGridContext>
}
